import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PieChart as PieChartIcon, TrendingUp, Save } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { API } from '../utils/api';

const COLORS = ["#22c55e", "#06b6d4", "#f59e0b", "#8b5cf6", "#f97316", "#ec4899"];

const formatCurrency = (value, currency = "EUR") => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
  }).format(value);
};

// Custom tooltip with better contrast
const CustomTooltip = ({ active, payload, isPercent, currency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-3 shadow-lg">
        <p className="text-white font-medium">{payload[0].name}</p>
        <p className="text-green-400 font-bold">
          {isPercent ? `${payload[0].value}%` : formatCurrency(payload[0].value, currency)}
        </p>
      </div>
    );
  }
  return null;
};

// Custom label to show percentage inside pie chart
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      className="text-xs font-bold"
      style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export default function Investments({ user }) {
  const [accounts, setAccounts] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({});
  const [composition, setComposition] = useState({
    equity_percent: 0,
    debt_percent: 0,
    commodities_percent: 0,
    cash_percent: 0,
    crypto_percent: 0,
    property_percent: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const defaultCurrency = user?.default_currency || "EUR";

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCurrency]);

  const fetchData = async () => {
    try {
      const [accountsRes, investmentsRes, ratesRes] = await Promise.all([
        axios.get(`${API}/accounts`),
        axios.get(`${API}/investments`),
        axios.get(`${API}/exchange-rates?base=${defaultCurrency}`)
      ]);
      
      // Filter only investment accounts
      const investmentAccounts = accountsRes.data.filter(
        acc => acc.account_type === "investment"
      );
      setAccounts(investmentAccounts);
      setInvestments(investmentsRes.data);
      setExchangeRates(ratesRes.data.rates || {});

      if (investmentAccounts.length > 0) {
        setSelectedAccount(investmentAccounts[0].account_id);
        loadComposition(investmentAccounts[0].account_id, investmentsRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load investment data");
    } finally {
      setLoading(false);
    }
  };

  // Convert amount to default currency using API rates
  const convertToDefaultCurrency = (amount, fromCurrency) => {
    if (fromCurrency === defaultCurrency) return amount;
    const rate = exchangeRates[fromCurrency.toLowerCase()];
    if (rate && rate > 0) {
      return amount / rate;
    }
    return amount;
  };

  const loadComposition = (accountId, investmentsList = investments) => {
    const investment = investmentsList.find(inv => inv.account_id === accountId);
    if (investment?.composition) {
      setComposition({
        equity_percent: investment.composition.equity_percent || 0,
        debt_percent: investment.composition.debt_percent || 0,
        commodities_percent: investment.composition.commodities_percent || 0,
        cash_percent: investment.composition.cash_percent || 0,
        crypto_percent: investment.composition.crypto_percent || 0,
        property_percent: investment.composition.property_percent || 0,
      });
    } else {
      setComposition({
        equity_percent: 0,
        debt_percent: 0,
        commodities_percent: 0,
        cash_percent: 0,
        crypto_percent: 0,
        property_percent: 0,
      });
    }
  };

  const handleAccountChange = (accountId) => {
    setSelectedAccount(accountId);
    loadComposition(accountId);
  };

  const updateComposition = (key, value) => {
    // Clamp value between 0 and 100
    const clampedValue = Math.min(100, Math.max(0, value));
    
    const total = Object.entries(composition)
      .filter(([k]) => k !== key)
      .reduce((sum, [, v]) => sum + v, 0);
    
    // Ensure total doesn't exceed 100
    const maxValue = 100 - total;
    const newValue = Math.min(clampedValue, maxValue);
    
    setComposition({ ...composition, [key]: newValue });
  };

  const handleInputChange = (key, inputValue) => {
    const value = inputValue === "" ? 0 : parseFloat(inputValue);
    if (!isNaN(value)) {
      updateComposition(key, value);
    }
  };

  const handleSave = async () => {
    if (!selectedAccount) return;

    setSaving(true);
    try {
      await axios.post(`${API}/investments`, {
        account_id: selectedAccount,
        composition: composition,
      });
      toast.success("Investment composition saved");
      fetchData();
    } catch (error) {
      console.error("Error saving composition:", error);
      toast.error("Failed to save composition");
    } finally {
      setSaving(false);
    }
  };

  const selectedAccountData = accounts.find(acc => acc.account_id === selectedAccount);
  const totalPercent = Object.values(composition).reduce((sum, v) => sum + v, 0);

  const pieData = [
    { name: "Equity", value: composition.equity_percent, color: COLORS[0] },
    { name: "Debt", value: composition.debt_percent, color: COLORS[1] },
    { name: "Commodities", value: composition.commodities_percent, color: COLORS[2] },
    { name: "Cash", value: composition.cash_percent, color: COLORS[3] },
    { name: "Crypto", value: composition.crypto_percent, color: COLORS[4] },
    { name: "Property", value: composition.property_percent, color: COLORS[5] },
  ].filter(item => item.value > 0);

  // Calculate overall portfolio allocation across all accounts (converted to default currency)
  const portfolioAllocation = accounts.reduce((acc, account) => {
    const investment = investments.find(inv => inv.account_id === account.account_id);
    if (investment?.composition) {
      const comp = investment.composition;
      const convertedBalance = convertToDefaultCurrency(account.balance, account.currency || "EUR");
      acc.equity += convertedBalance * (comp.equity_percent || 0) / 100;
      acc.debt += convertedBalance * (comp.debt_percent || 0) / 100;
      acc.commodities += convertedBalance * (comp.commodities_percent || 0) / 100;
      acc.cash += convertedBalance * (comp.cash_percent || 0) / 100;
      acc.crypto += convertedBalance * (comp.crypto_percent || 0) / 100;
      acc.property += convertedBalance * (comp.property_percent || 0) / 100;
    }
    return acc;
  }, { equity: 0, debt: 0, commodities: 0, cash: 0, crypto: 0, property: 0 });

  const portfolioPieData = [
    { name: "Equity", value: portfolioAllocation.equity },
    { name: "Debt", value: portfolioAllocation.debt },
    { name: "Commodities", value: portfolioAllocation.commodities },
    { name: "Cash", value: portfolioAllocation.cash },
    { name: "Crypto", value: portfolioAllocation.crypto },
    { name: "Property", value: portfolioAllocation.property },
  ].filter(item => item.value > 0);

  if (loading) {
    return (
      <Layout user={user} pageTitle="Investments">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} pageTitle="Investments">
      <div data-testid="investments-page" className="space-y-8">
        {/* Page Header */}
        <PageHeader pageKey="investments" />
        
        {accounts.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">No Investment Accounts</h3>
              <p className="text-gray-400 mb-6">
                Create an investment account first to track your portfolio composition.
              </p>
              <Button 
                asChild
                className="bg-green-500 hover:bg-green-400 text-black rounded-full px-6"
              >
                <a href="/accounts">Go to Accounts</a>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Account Composition Editor */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-white">
                    Investment Composition
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="text-gray-300">Select Account</Label>
                    <Select value={selectedAccount} onValueChange={handleAccountChange}>
                      <SelectTrigger className="mt-1 bg-secondary border-border text-white" data-testid="investment-account-select">
                        <SelectValue placeholder="Select an account" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {accounts.map((account) => (
                          <SelectItem key={account.account_id} value={account.account_id} className="text-gray-300 focus:text-white focus:bg-secondary">
                            {account.name} - {formatCurrency(account.balance, account.currency)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedAccount && (
                    <>
                      <div className="space-y-5">
                        {/* Equity */}
                        <div className="p-4 bg-secondary/50 rounded-xl border border-border">
                          <div className="flex items-center justify-between mb-3">
                            <Label className="text-green-400 font-semibold">Equity</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={composition.equity_percent}
                                onChange={(e) => handleInputChange("equity_percent", e.target.value)}
                                className="w-20 h-8 text-right bg-secondary border-border text-white number-display"
                                data-testid="equity-input"
                              />
                              <span className="text-gray-400">%</span>
                            </div>
                          </div>
                          <Slider
                            value={[composition.equity_percent]}
                            onValueChange={([value]) => updateComposition("equity_percent", value)}
                            max={100}
                            step={1}
                            className="[&_[role=slider]]:bg-green-500 [&_[role=slider]]:border-green-500"
                            data-testid="equity-slider"
                          />
                        </div>

                        {/* Debt */}
                        <div className="p-4 bg-secondary/50 rounded-xl border border-border">
                          <div className="flex items-center justify-between mb-3">
                            <Label className="text-cyan-400 font-semibold">Debt</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={composition.debt_percent}
                                onChange={(e) => handleInputChange("debt_percent", e.target.value)}
                                className="w-20 h-8 text-right bg-secondary border-border text-white number-display"
                                data-testid="debt-input"
                              />
                              <span className="text-gray-400">%</span>
                            </div>
                          </div>
                          <Slider
                            value={[composition.debt_percent]}
                            onValueChange={([value]) => updateComposition("debt_percent", value)}
                            max={100}
                            step={1}
                            className="[&_[role=slider]]:bg-cyan-500 [&_[role=slider]]:border-cyan-500"
                            data-testid="debt-slider"
                          />
                        </div>

                        {/* Commodities */}
                        <div className="p-4 bg-secondary/50 rounded-xl border border-border">
                          <div className="flex items-center justify-between mb-3">
                            <Label className="text-amber-400 font-semibold">Commodities</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={composition.commodities_percent}
                                onChange={(e) => handleInputChange("commodities_percent", e.target.value)}
                                className="w-20 h-8 text-right bg-secondary border-border text-white number-display"
                                data-testid="commodities-input"
                              />
                              <span className="text-gray-400">%</span>
                            </div>
                          </div>
                          <Slider
                            value={[composition.commodities_percent]}
                            onValueChange={([value]) => updateComposition("commodities_percent", value)}
                            max={100}
                            step={1}
                            className="[&_[role=slider]]:bg-amber-500 [&_[role=slider]]:border-amber-500"
                            data-testid="commodities-slider"
                          />
                        </div>

                        {/* Cash */}
                        <div className="p-4 bg-secondary/50 rounded-xl border border-border">
                          <div className="flex items-center justify-between mb-3">
                            <Label className="text-purple-400 font-semibold">Cash</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={composition.cash_percent}
                                onChange={(e) => handleInputChange("cash_percent", e.target.value)}
                                className="w-20 h-8 text-right bg-secondary border-border text-white number-display"
                                data-testid="cash-input"
                              />
                              <span className="text-gray-400">%</span>
                            </div>
                          </div>
                          <Slider
                            value={[composition.cash_percent]}
                            onValueChange={([value]) => updateComposition("cash_percent", value)}
                            max={100}
                            step={1}
                            className="[&_[role=slider]]:bg-purple-500 [&_[role=slider]]:border-purple-500"
                            data-testid="cash-slider"
                          />
                        </div>

                        {/* Crypto */}
                        <div className="p-4 bg-secondary/50 rounded-xl border border-border">
                          <div className="flex items-center justify-between mb-3">
                            <Label className="text-orange-400 font-semibold">Crypto</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={composition.crypto_percent}
                                onChange={(e) => handleInputChange("crypto_percent", e.target.value)}
                                className="w-20 h-8 text-right bg-secondary border-border text-white number-display"
                                data-testid="crypto-input"
                              />
                              <span className="text-gray-400">%</span>
                            </div>
                          </div>
                          <Slider
                            value={[composition.crypto_percent]}
                            onValueChange={([value]) => updateComposition("crypto_percent", value)}
                            max={100}
                            step={1}
                            className="[&_[role=slider]]:bg-orange-500 [&_[role=slider]]:border-orange-500"
                            data-testid="crypto-slider"
                          />
                        </div>

                        {/* Property */}
                        <div className="p-4 bg-secondary/50 rounded-xl border border-border">
                          <div className="flex items-center justify-between mb-3">
                            <Label className="text-pink-400 font-semibold">Property</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={composition.property_percent}
                                onChange={(e) => handleInputChange("property_percent", e.target.value)}
                                className="w-20 h-8 text-right bg-secondary border-border text-white number-display"
                                data-testid="property-input"
                              />
                              <span className="text-gray-400">%</span>
                            </div>
                          </div>
                          <Slider
                            value={[composition.property_percent]}
                            onValueChange={([value]) => updateComposition("property_percent", value)}
                            max={100}
                            step={1}
                            className="[&_[role=slider]]:bg-pink-500 [&_[role=slider]]:border-pink-500"
                            data-testid="property-slider"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div>
                          <p className="text-sm text-gray-500">Total Allocation</p>
                          <p className={`text-xl font-bold number-display ${totalPercent === 100 ? "text-green-400" : "text-amber-400"}`}>
                            {totalPercent}%
                          </p>
                          {totalPercent !== 100 && (
                            <p className="text-xs text-amber-400">Should equal 100%</p>
                          )}
                        </div>
                        <Button 
                          onClick={handleSave}
                          disabled={saving}
                          className="bg-green-500 hover:bg-green-400 text-black rounded-full px-6 font-semibold"
                          data-testid="save-composition-btn"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {saving ? "Saving..." : "Save"}
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Account Composition Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-white">
                    {selectedAccountData?.name || "Account"} Allocation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {pieData.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                            label={renderCustomLabel}
                            labelLine={false}
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip isPercent={true} />} />
                          <Legend wrapperStyle={{ color: "#a1a1aa" }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-gray-500">
                      Set allocation percentages to see the chart
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Overall Portfolio */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-white">
                    Overall Portfolio Allocation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {portfolioPieData.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={portfolioPieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={80}
                              paddingAngle={2}
                              dataKey="value"
                              label={renderCustomLabel}
                              labelLine={false}
                            >
                              {portfolioPieData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip currency={defaultCurrency} />} />
                            <Legend wrapperStyle={{ color: "#a1a1aa" }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="space-y-3">
                        {portfolioPieData.map((item, index) => (
                          <div key={item.name} className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl border border-border">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              />
                              <span className="font-medium text-gray-300">{item.name}</span>
                            </div>
                            <span className="font-bold text-white number-display">
                              {formatCurrency(item.value, user?.default_currency || "EUR")}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-gray-500">
                      Add investment compositions to your accounts to see the overall portfolio allocation
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}
      </div>
    </Layout>
  );
}
